<template>
  <div class="d-flex position-relative flex-grow-1 h-0 flex-column">
    <!-- MAP CANVAS -->
    <div class="d-flex z-index-1 flex-grow-1 h-0 flex-column">
      <GmbMapCanvas
          :activatedMapLayerPks="activatedMapLayerPks"
          :activatedMapOptionSlugs="activatedMapOptionSlugs"
          :individuals="colorizedIndividuals"
          :mapConfiguration="mapConfiguration"
          :selectedMapBackgroundIdx="selectedMapBackgroundIdx"
          :organizations="organizations"
          ref="gmbMapCanvas"
          :transportFilters="transportFilters"
          :userAccount="userAccount"
          @go-to-individuals-edit-page="
          (individualIds) => $emit('go-to-individuals-edit-page', individualIds)
        "
          @map-image-export="onMapImageExport"
      />
    </div>

    <!-- MAP ZOOM CONTROLS -->
    <div
        class="d-flex position-absolute z-index-2 bottom-0 left-50 h-100 flex-column justify-content-end"
    >
      <div class="d-flex position-relative mb-3 flex-column align-items-center">
        <div class="d-flex position-absolute bottom-0 flex-column">
          <div
              class="d-flex p-2 align-items-center rounded-1 gmb-map-zoom-controls-box"
          >

            <b-button
                class="d-flex p-0 justify-content-center align-items-center text-size-reset rounded-1 gmb-map-zoom-control"
                variant="primary"
                @click="switchSidePanelVisibility()"
                title="Masquer/afficher le panneau latéral"
            >
              <FdText fa>
                <i class="fa fa-sidebar" style="font-size: 120%"/>
              </FdText>
            </b-button>

            <b-button
                class="d-flex ml-2 p-0 justify-content-center align-items-center text-size-reset rounded-1 gmb-map-zoom-control"
                variant="primary"
                @click="updateCurrentZoomLevel(-1)"
                title="Dézoomer"
            >
              <FdText fa>
                <i class="fa-solid fa-minus"/>
              </FdText>
            </b-button>

            <b-button
                class="d-flex ml-2 p-0 justify-content-center align-items-center text-size-reset rounded-1 gmb-map-zoom-control"
                variant="primary"
                @click="updateCurrentZoomLevel(1)"
                title="Zoomer"
            >
              <FdText fa>
                <i class="fa-solid fa-plus"/>
              </FdText>
            </b-button>

            <b-button
                class="d-flex ml-2 p-0 justify-content-center align-items-center text-size-reset rounded-1 gmb-map-zoom-control"
                variant="primary"
                @click="toggleFullscreen()"
                title="Plein écran"
            >
              <FdText fa>
                <i class="fa-solid fa-maximize"/><!-- see "fa-expand" too as an alternative! -->
              </FdText>
            </b-button>

            <b-button
                v-if="userCanAccessPdfExporter"
                class="d-flex ml-2 p-0 justify-content-center align-items-center text-size-reset rounded-1 gmb-map-zoom-control"
                variant="primary"
                @click="exportToReportingSection()"
                title="Ajouter au PDF"
            >
              <FdText fa>
                <i class="fa-solid fa-floppy-disk"/>
              </FdText>
            </b-button>

          </div>
        </div>
      </div>
    </div>

    <!-- MAP COLORIZERS -->
    <div class="d-flex position-absolute z-index-2 top-0 w-0 h-50 flex-column">
      <div
          class="d-flex mt-3 mb-2 ml-3 flex-grow-1 h-0 flex-column gmb-block--floating-box-wrapper"
      >
        <GmbMapColorizers
            :activatedMapColorizerSlug="activatedMapColorizerSlug"
            :allMapColorizerSlugs="allMapColorizerSlugs"
            class="maxh-100"
            :mapColorizerCategories="mapColorizerCategories"
            @activated-map-colorizer-changed="
            (newMapColorizerSlug) => {
              this.activatedMapColorizerSlug = newMapColorizerSlug;
            }
          "
        />
      </div>
    </div>

    <!-- MAP LEGEND -->
    <div
        class="d-flex position-absolute z-index-2 bottom-0 w-0 h-50 flex-column justify-content-end"
    >
      <div
          class="d-flex mt-2 mb-3 ml-3 flex-grow-1 h-0 flex-column justify-content-end gmb-block--floating-box-wrapper"
      >
        <GmbMapLegend
            :activatedMapColorizer="activatedMapColorizer"
            :activatedMapOptionSlugs="activatedMapOptionSlugs"
            class="maxh-100"
            ref="gmbMapLegend"
        />
      </div>
    </div>

    <!-- MAP LAYERS + MAP OPTIONS -->
    <div
        class="d-flex position-absolute z-index-2 top-0 right-0 h-100 flex-column"
    >
      <!-- MAP LAYERS -->
      <div class="d-flex mt-3 mb-2 ml-n3 h-50 flex-column">
        <div
            class="d-flex flex-grow-1 w-0 h-0 flex-column gmb-block--floating-box-wrapper gmb-block--floating-box-wrapper--right"
        >
          <GmbMapLayers
              :activatedMapLayerPks="activatedMapLayerPks"
              class="maxh-100"
              :mapLayerCategories="mapLayerCategories"
              @activated-map-layers-changed="
              (newActivatedMapLayerPks) =>
                onActivatedMapLayersChanged(newActivatedMapLayerPks)
            "
          />
        </div>
      </div>

      <!-- MAP OPTIONS -->
      <div class="d-flex mt-2 mb-3 ml-n3 h-50 flex-column">
        <div
            class="d-flex flex-grow-1 w-0 h-0 flex-column justify-content-end gmb-block--floating-box-wrapper gmb-block--floating-box-wrapper--right"
        >
          <GmbMapOptions
              :activatedMapOptionSlugs="activatedMapOptionSlugs"
              :mapConfiguration="mapConfiguration"
              :selectedMapBackgroundIdx="selectedMapBackgroundIdx"
              class="maxh-100"
              @activated-map-options-changed="
              (newActivatedMapOptionSlugs) =>
                onActivatedMapOptionsChanged(newActivatedMapOptionSlugs)
            "
              @selected-map-background-idx-changed="
              (newSelectedMapBackgroundIdx) => onNewSelectedMapBackgroundIdx(newSelectedMapBackgroundIdx)
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {convertAsKeywords} from "@/helpers";

import FdText from "@/flexdesign/Text";

import GmbMapCanvas from "@/views/Observatory/Map/MapCanvas";
import GmbMapColorizers from "@/views/Observatory/Map/MapColorizers";
import GmbMapLayers from "@/views/Observatory/Map/MapLayers";
import GmbMapLegend from "@/views/Observatory/Map/MapLegend";
import GmbMapOptions from "@/views/Observatory/Map/MapOptions";

import {openToast} from "@/helpers.js";

export default {
  name: "GmbMap",

  components: {
    FdText,
    GmbMapCanvas,
    GmbMapColorizers,
    GmbMapLayers,
    GmbMapLegend,
    GmbMapOptions,
  },

  props: {
    mapColorizerCategories: Array,
    individuals: Array,
    mapConfiguration: Object,
    organizations: Array,
    transportFilters: Array,
    userAccount: Object,
    userCanAccessPdfExporter: Boolean,
  },

  data() {
    return {
      activatedMapColorizerSlug: undefined, // By default, activate the Organizations mapColorizer
      activatedMapLayerPks: undefined,
      activatedMapOptionSlugs: [],
      selectedMapBackgroundIdx: 0,
      individualColorCache: new Map(),
    };
  },

  computed: {
    activatedMapColorizer() {
      if (!this.mapColorizerCategories || this.activatedMapColorizerSlug == undefined)
        return undefined;

      return this.findActivatedMapColorizer(
          this.mapColorizerCategories,
          this.activatedMapColorizerSlug
      );
    },

    allMapColorizerSlugs() {
      let mapColorizerSlugs = [];

      if (this.mapColorizerCategories)
        this.mapColorizerCategories.forEach((mapColorizerCategory) => {
          mapColorizerCategory.mapColorizers.forEach((mapColorizer) => {
            mapColorizerSlugs.push(mapColorizer.identifier);
          });
        });

      return mapColorizerSlugs;
    },

    colorizedIndividuals() {
      //[LOG]console.log("colorizedIndividuals START");
      if (!this.individuals) return undefined;

      if (!this.activatedMapColorizer) return this.individuals; // Let the Canva choose the default color

      let colorizedIndividuals = structuredClone(this.individuals);

      //let _currentActivatedMapColorizerSlug = this.activatedMapColorizerSlug;
      for (let individual of colorizedIndividuals) {
        let color = this.individualColorCache.get(individual.pk)
        if (!color) {
          let colorKey = this.activatedMapColorizer.colorKey(individual);
          color = this.activatedMapColorizer.colorMapper[colorKey];
          this.individualColorCache.set(individual.pk, color);
        } else {
          //console.log("COLOR CACHE HIT!!")
        }
        individual.color = color
        //this.$set(individual, "color", this.activatedMapColorizer.colorMapper[colorKey]);  // NOPE
        //individual._cached_colorizer_slug = _currentActivatedMapColorizerSlug;
      }

      //[LOG]console.log("colorizedIndividuals END");
      return colorizedIndividuals;
    },

    mapLayerCategories() {
      return this.mapConfiguration
          ? this.mapConfiguration.mapLayerCategories
          : [];
    },
  },

  watch: {
    mapColorizerCategories: {
      handler: function (newMapColorizerCategories) {
        if (
            newMapColorizerCategories &&
            newMapColorizerCategories.length &&
            (!this.activatedMapColorizerSlug ||
                !this.findActivatedMapColorizer(
                    newMapColorizerCategories,
                    this.activatedMapColorizerSlug
                ))
        )
          this.activatedMapColorizerSlug =
              newMapColorizerCategories[0].mapColorizers[0].identifier; // Initialize legend to first entry
      },
      immediate: true,
    },
    activatedMapColorizerSlug: function (value){
      this.individualColorCache.clear();
    },

    mapConfiguration: {
      handler: function (newMapConfiguration) {
        /* When map configuration changes, we RESET displayed map layers selected by user */
        if (!newMapConfiguration) return;

        let activatedMapLayerPks = [];
        for (let mapLayerCategory of newMapConfiguration.mapLayerCategories)
          for (let mapLayer of mapLayerCategory.mapLayers)
            if (mapLayer.defaultVisibility)
              activatedMapLayerPks.push(mapLayer.pk.toString());
        this.activatedMapLayerPks = activatedMapLayerPks;

        //console.log(">>>>>>> setting this.selectedMapBackgroundIdx", newMapConfiguration.defaultMapBackgroundIdx)
        this.selectedMapBackgroundIdx = newMapConfiguration.defaultMapBackgroundIdx;  // SETUP
      },
      immediate: true,
    },
  },

  beforeCreate() {
  },

  created() {
  },

  beforeMount() {
  },

  mounted() {
  },

  beforeUpdate() {
  },

  updated() {
  },

  beforeUnmount() {
  },

  unmounted() {
  },

  methods: {
    findActivatedMapColorizer(mapColorizerCategories, activatedMapColorizerSlug) {
      for (let mapColorizerGroup of mapColorizerCategories) {
        let activatedMapColorizer = mapColorizerGroup.mapColorizers.find(
            (mapColorizer) => mapColorizer.identifier == activatedMapColorizerSlug
        );
        if (activatedMapColorizer) return activatedMapColorizer;
      }

      return undefined;
    },

    onActivatedMapLayersChanged(newActivatedMapLayerPks) {
      this.activatedMapLayerPks = newActivatedMapLayerPks;
    },

    onActivatedMapOptionsChanged(newActivatedMapOptionSlugs) {
      this.activatedMapOptionSlugs = newActivatedMapOptionSlugs;
    },

    onNewSelectedMapBackgroundIdx(newSelectedMapBackgroundIdx) {
      this.selectedMapBackgroundIdx = newSelectedMapBackgroundIdx;
    },

    updateCurrentZoomLevel(step) {
      this.$refs.gmbMapCanvas.updateCurrentZoomLevel(step);
    },

    toggleFullscreen() {
      this.$refs.gmbMapCanvas.toggleFullscreen();
    },

    exportToReportingSection() {
      openToast("Opération commencée", "Export de la carte actuelle en cours, ne touchez à rien svp");
      this.$refs.gmbMapCanvas.exportMapAsPngImageDataUrl();  // Will emit event when ready
    },

    getMapLayerCategoriesSummary() {
      let categories = [];
      for (const category of this.mapLayerCategories) {
        let mapLayersSummary = [];
        for (let mapLayer of category.mapLayers) {
          if (this.activatedMapLayerPks.includes(mapLayer.pk.toString()))
            mapLayersSummary.push(mapLayer.label)
        }
        if (mapLayersSummary.length) {
          categories.push([
            category.label, category.slug, mapLayersSummary
          ]);
        }
      }

      return {
        map_layer_categories: categories,  // Might be empty
      }
    },

    onMapImageExport(pngImageDataUrl) {
      let mapLegend = this.$refs.gmbMapLegend.getMapLegendSummary()
      let mapLayerCategories = this.getMapLayerCategoriesSummary()

      let sectionData = Object.assign(
          {map_image: pngImageDataUrl},
          mapLegend);
      sectionData = Object.assign(
          sectionData,
          mapLayerCategories);
      //console.log(">> Map chapterData", chapterData)
      this.$emit("export-reporting-section", sectionData);
    },

    switchSidePanelVisibility() {
      //console.log("account menu switchSidePanelVisibility called", this.isSidePanelOpen);
      this.$emit("switch-side-panel-visibility");
    }
  },
};
</script>

<style lang="scss">
@import "@/sass/settings/colors";

/* =========================================================================
 * MAP ZOOM CONTROLS BOX
 * ========================================================================= */

.gmb-map-zoom-controls-box {
  opacity: 0.7;
  transition: all 0.175s ease;
  background-color: rgba($gmb-color-theme-1, 0.25);

  &:hover {
    opacity: 0.9;
    background-color: rgba($gmb-color-theme-1, 0.375);
    transition: all 0.175s ease;
  }
}

.gmb-map-zoom-control {
  width: 24px;
  height: 24px;
}
</style>
